import { registerPlugin } from "@capacitor/core";

/**
 * Android アプリで SMS User Consent API を使い、端末に届いた SMS から認証コードを取り出す自作プラグイン（Trello #157）。
 * 実体は android_custom/app/src/main/java/jp/co/propally/SmsUserConsentPlugin.java（yarn build-android で生成物へコピーされる）。
 * Capacitor の WebView では WebOTP API が使えないため、Android アプリだけこの経路を使う。
 * iOS は OS の AutoFill（autocomplete="one-time-code"）、ブラウザは WebOTP API に任せるので、ここは Android 以外で呼ばない。
 */
export interface SmsUserConsentPlugin {
  /**
   * SMS の受信待ちを始める。コードを含む SMS が届くと OS の許可ダイアログが出て、
   * 承認された本文が Propally の SMS（本文に "Propally" を含む）なら、取り出した数字のコードで resolve する。
   * 拒否された・Propally 以外の SMS だった・桁数ちょうどのコードが無かったときは reject せず受信待ちを張り直す
   * （1 回の start() につき 3 回まで。超えたら reject）。タイムアウト（Play Services 側で約 5 分）・stop()・
   * Play Services が使えない端末では reject する。
   */
  start(options: { codeLength: number }): Promise<{ code: string }>;
  /** 受信待ちをやめる。待機中の start() は reject される。 */
  stop(): Promise<void>;
}

let plugin: SmsUserConsentPlugin | undefined;

/**
 * registerPlugin は import 時ではなく最初の呼び出しで 1 回だけ実行し、以後は同じインスタンスを使う（遅延登録）。
 * 画面テストの多くは @capacitor/core を `Capacitor` だけの部分モックに差し替えるため、import しただけで
 * registerPlugin が走ると、この経路を使わないテストまで読み込み時に落ちてしまう。
 */
const getPlugin = (): SmsUserConsentPlugin => {
  if (!plugin) plugin = registerPlugin<SmsUserConsentPlugin>("SmsUserConsent");
  return plugin;
};

/**
 * start / stop は async にして、登録失敗やネイティブ側の同期例外も reject として呼び出し側（useSmsCodeAutofill の catch）へ届ける。
 */
export const SmsUserConsent: SmsUserConsentPlugin = {
  start: async (options) => getPlugin().start(options),
  stop: async () => getPlugin().stop(),
};
